import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  PatchRequest,
  useProcessesProcessPatchMutation,
} from "@/services/backend/processes/api/process-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import t from "@/lang/lang";
import { TextInput } from "@/shared/components/form/text-input";
import { useNumbersProcessNumberShowQuery } from "@/services/backend/numbers/api/process-number-api";
import { useShowCompanyQuery } from "@/services/backend/company/api/company";
import { DateInput } from "@/shared/components/form/date-input";
import { Process, ViewModel } from "@/services/backend/processes/types/process";
import { BranchesSelect } from "@/services/backend/company/components/branches-select";
import { ServiceObjectsSelect } from "@/services/backend/customers/components/service-objects-select";
import { useState } from "react";
import {
  inputValidationErrorMessages,
  InputValidationErrors,
} from "@/shared/components/errors/input-error-messages";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { CustomerSelect } from "@/services/backend/customers/components/customer-select";
import { Field, FieldLabel } from "@/shared/components/ui/field";
import { Input } from "@/shared/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Info } from "lucide-react";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { useRefEffect } from "@/shared/hooks/use-ref-effect";

export function ProcessInformationCard({
  process,
  withServiceObjects = true,
}: {
  process: ViewModel;
  withServiceObjects?: boolean;
}) {
  const processNumber = useNumbersProcessNumberShowQuery({
    processId: process.id,
  });

  return (
    <PersistenceContextProvider>
      <Card>
        <CardHeader>
          <CardTitle>
            {t("Vorgang")} {processNumber?.data?.string}
          </CardTitle>
          <CardDescription>
            {t(
              "Bitte beachten Sie bei Änderungen am Vorgang die Informationen im Info-Dialog.",
            )}
          </CardDescription>
          <CardAction className="flex gap-2">
            <PersistenceStateUsingContext />
            <ProcessDocumentationDialog />
          </CardAction>
        </CardHeader>
        <CardContent>
          <ProcessPatchFields
            process={process}
            withServiceObjects={withServiceObjects}
          />
        </CardContent>
      </Card>
    </PersistenceContextProvider>
  );
}

const removalNote = t(
  "Die Auswahl hat keinen Einfluss auf bestehende Dokumente des Vorgangs",
);

const lastBranchNote = t(
  "Mindestens eine Niederlassung muss ausgewählt bleiben",
);

function ProcessPatchFields({
  process,
  withServiceObjects,
}: {
  process: Process;
  withServiceObjects: boolean;
}) {
  const [request, setRequest] = useState<PatchRequest>({
    id: process.id,
    // We initialize the fields only if there is valid input,
    // otherwise the patch will, e.g. send an empty topic.
    topic: process.topic !== "" ? process.topic : undefined,
    customerId: process.customerId,
    branches: process.branches,
    serviceObjects: process.serviceObjects,
    owningUserId: process.owningUserId,
  });
  const mutation = useProcessesProcessPatchMutation();
  const company = useShowCompanyQuery({ id: process?.companyId ?? "" });

  // Update if updated through cpy dialog.
  useRefEffect(process.customerId, () =>
    setRequest({
      id: process.id,
      // We initialize the fields only if there is valid input,
      // otherwise the patch will, e.g. send an empty topic.
      topic: process.topic !== "" ? process.topic : undefined,
      customerId: process.customerId,
      branches: process.branches,
      serviceObjects: process.serviceObjects,
      owningUserId: process.owningUserId,
    }),
  );

  const { error } = useDebouncedMutation(request, setRequest, mutation, {
    id: process.id,
    // We initialize the fields only if there is valid input,
    // otherwise the patch will, e.g. send an empty topic.
    topic: process.topic !== "" ? process.topic : undefined,
    customerId: process.customerId,
    branches: process.branches,
    serviceObjects: process.serviceObjects,
    owningUserId: process.owningUserId,
  });

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <TextInput
        label={t("Thema")}
        field="topic"
        text={request.topic}
        onTextChange={(topic) => setRequest({ ...request, topic })}
        errors={inputValidationErrorMessages(error, "topic")}
        className="md:col-span-2"
        hint={t(
          "Interne Bezeichnung für Vorgang. Sollte für alle Nutzer verständlich sein.",
        )}
      />
      <Field>
        <FieldLabel>{t("Kunde")}</FieldLabel>
        <CustomerSelect
          value={request.customerId}
          onValueChange={(customerId) => setRequest({ ...request, customerId })}
          note={removalNote}
        />
      </Field>
      {withServiceObjects && (
        <Field>
          <FieldLabel>{t("Service Objekte")}</FieldLabel>
          <ServiceObjectsSelect
            customerId={request.customerId}
            value={request.serviceObjects}
            onValueChange={(serviceObjects) =>
              setRequest({ ...request, serviceObjects })
            }
            note={removalNote}
            disabled={!request.customerId}
          />
          <InputValidationErrors error={error} field="serviceObjects" />
        </Field>
      )}
      <Field>
        <FieldLabel>{t("Unternehmen")}</FieldLabel>
        <Input value={company.data?.name ?? ""} disabled />
      </Field>
      <Field>
        <FieldLabel>{t("Niederlassungen")}</FieldLabel>
        <BranchesSelect
          value={request.branches}
          // A process must keep at least one branch: the backend
          // authorizes access to it by its branches.
          onValueChange={(branches) =>
            branches.length > 0 && setRequest({ ...request, branches })
          }
          companyFilter={{ active: true, values: [process.companyId] }}
          note={removalNote}
        />
        <p className="text-muted-foreground text-sm">{lastBranchNote}</p>
        <InputValidationErrors error={error} field="branches" />
      </Field>
      <DateInput
        label={t("Erstellt am")}
        date={process!.createdAt}
        onDateChange={() => {}}
        errors={[]}
        disabled
      />
      <DateInput
        label={t("Aktualisiert am")}
        date={process!.updatedAt}
        onDateChange={() => {}}
        errors={[]}
        disabled
      />
      <RTKQueryErrorAlert error={error} className="md:col-span-2" />
    </div>
  );
}

function ProcessDocumentationDialog() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon">
          <Info />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:min-w-fit">
        <DialogHeader>
          <DialogTitle>{t("Was ist ein Vorgang?")}</DialogTitle>
        </DialogHeader>
        <div>
          <div className="text-lg font-semibold">
            {t("Funktionen eines Vorgangs")}
          </div>
          <ol className="ml-6 list-decimal">
            <li>
              {t(
                'Ein Vorgang gruppiert Dokumente welche zu einem bestimmten Thema / Objekt / Verkaufschance / ... gehören. Was in einem Vorgang behandelt wird und wie lange dieser "lebt", ist Entscheidung des Nutzers.',
              )}
            </li>
            <li>
              {t(
                "Ein Vorgang diehnt als Datenbasis für Dokumente welche in ihm erstellt werden.",
              )}
            </li>
          </ol>
          <p className="mt-1">
            {t(
              "Die Daten eines Vorgangs können sich unter bestimmten Umständen im Lauf der Zeit ändern. Das ist zwar grundsätzlich ungewöhnlich, kann aber vorkommen. Nutzer sollten sich aber klar machen, dass ein grundloses ändern auch ungewollte Effekte haben kann.",
            )}
          </p>
        </div>
        <div>
          <div className="text-lg font-semibold">
            {t("Wie wirken sich Änderungen am Vorgang auf Dokumente aus?")}
          </div>
          <ul className="ml-6 list-disc">
            <li>
              {t(
                "Wenn ein neues Dokument erstellt wird, werden hierfür immer die akutellen Daten des Vorgangs genutzt.",
              )}
            </li>
            <li>
              {t(
                "Wenn die Daten eines Vorgang geändert werden, ändert dies nichts an bestehenden Dokumenten. Egal ob diese festgeschrieben sind oder nicht.",
              )}
            </li>
            <li>
              {t(
                "Wenn die Daten eines Vorgang geändert werden und nicht mehr zu den Daten eines Dokuments passen welches im Vorgang erstellt wurde, so bleibt das Dokument trotzdem Teil des Vorgangs.",
              )}
            </li>
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}
